/**
 * IndexedDB persistence for File System Access API handles (Chromium only).
 *
 * A picked `File` is a permission-scoped handle to bytes on disk; a page reload
 * revokes it, which is why plain uploads must re-select the file to resume. A
 * `FileSystemFileHandle`, however, is structured-cloneable and survives in
 * IndexedDB across reloads — so on the next load we can re-acquire read access
 * with a single permission grant (`requestPermission`) instead of a full
 * file-picker navigation. Non-Chromium browsers fall back to manual re-select.
 *
 * Handles are keyed by the same `mpu:<name>:<size>:<lastModified>` string used
 * for the localStorage resume session, so the two correlate 1:1.
 */

const DB_NAME = 'circa-upload-handles';
const STORE = 'handles';
const DB_VERSION = 1;

/** Whether the current browser exposes the File System Access picker. */
export function supportsFsAccess(): boolean {
  return typeof window !== 'undefined' && 'showOpenFilePicker' in window;
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => { req.result.createObjectStore(STORE); };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
  return openDb().then((db) => {
    if (!db) return null;
    return new Promise<T | null>((resolve) => {
      try {
        const tx = db.transaction(STORE, mode);
        const req = run(tx.objectStore(STORE));
        req.onsuccess = () => resolve((req.result as T) ?? null);
        req.onerror = () => resolve(null);
        tx.oncomplete = () => db.close();
      } catch { resolve(null); }
    });
  });
}

/** Persist a picked file's handle so a future reload can re-acquire it. */
export async function saveFileHandle(key: string, handle: unknown): Promise<void> {
  await withStore('readwrite', (s) => s.put(handle, key));
}

/** Recover a stored handle (or null if none / unsupported). */
export async function loadFileHandle(key: string): Promise<any | null> {
  return withStore<any>('readonly', (s) => s.get(key));
}

/** Forget a stored handle (upload completed or dismissed). */
export async function deleteFileHandle(key: string): Promise<void> {
  await withStore('readwrite', (s) => s.delete(key));
}
